import { AxiosHeaders, isAxiosError } from "axios";

export type HttpErrorKind =
  | "badRequest"
  | "unauthorized"
  | "forbidden"
  | "notFound"
  | "conflict"
  | "server"
  | "network"
  | "timeout"
  | "unknown";

type HttpClientErrorInit = {
  kind: HttpErrorKind;
  message: string;
  status: number | null;
  code: string | null;
  method: string | null;
  url: string | null;
  serverMessage: string | null;
  // 생략할 수 있다. Retry-After 를 전달하는 경로(normalizeHttpError)만 명시한다.
  retryAfterSeconds?: number | null;
  details: unknown;
  cause: unknown;
};

export class HttpClientError extends Error {
  readonly kind: HttpErrorKind;
  readonly status: number | null;
  readonly code: string | null;
  readonly method: string | null;
  readonly url: string | null;
  readonly serverMessage: string | null;
  // 서버가 Retry-After 헤더로 지정한 재시도 대기(초). 없거나 해석할 수 없으면 null.
  readonly retryAfterSeconds: number | null;
  readonly details: unknown;
  readonly cause: unknown;

  constructor(init: HttpClientErrorInit) {
    super(init.message);
    this.name = "HttpClientError";
    this.kind = init.kind;
    this.status = init.status;
    this.code = init.code;
    this.method = init.method;
    this.url = init.url;
    this.serverMessage = init.serverMessage;
    this.retryAfterSeconds = init.retryAfterSeconds ?? null;
    this.details = init.details;
    this.cause = init.cause;
  }

  toLogContext(): Record<string, unknown> {
    return {
      kind: this.kind,
      status: this.status,
      code: this.code,
      method: this.method,
      url: this.url,
      serverMessage: this.serverMessage,
      retryAfterSeconds: this.retryAfterSeconds,
      details: this.details,
    };
  }
}

export function isHttpClientError(error: unknown): error is HttpClientError {
  return error instanceof HttpClientError;
}

// 409 Conflict 판별. 멱등 작업(중복 북마크/생성 등)에서 409 를 성공으로 처리할 때 쓴다.
export function isConflictError(error: unknown): boolean {
  return isHttpClientError(error) && error.kind === "conflict";
}

export function normalizeHttpError(error: unknown): HttpClientError {
  // axios 원본 에러를 화면/로그가 함께 쓰는 안정적인 타입으로 바꾼다.
  if (isHttpClientError(error)) {
    return error;
  }

  if (!isAxiosError(error)) {
    return new HttpClientError({
      kind: "unknown",
      message: "요청을 완료하지 못했습니다.",
      status: null,
      code: null,
      method: null,
      url: null,
      serverMessage: null,
      retryAfterSeconds: null,
      details: null,
      cause: error,
    });
  }

  const status = error.response?.status ?? null;
  const transportCode = error.code ?? null;
  const code = extractServerCode(error.response?.data) ?? transportCode;
  const kind = resolveHttpErrorKind(status, transportCode);
  const serverMessage = extractServerMessage(error.response?.data);

  return new HttpClientError({
    kind,
    message: resolveHttpErrorMessage(kind, status, serverMessage),
    status,
    code,
    method: error.config?.method?.toUpperCase() ?? null,
    url: error.config?.url ?? null,
    serverMessage,
    retryAfterSeconds: extractRetryAfterSeconds(error.response?.headers),
    details: error.response?.data ?? null,
    cause: error,
  });
}

// 에러 본문이 Blob 이면(`responseType: "blob"` 요청의 실패 응답) 서버 code/message 를 추출할 수
// 없다. 그래서 정규화 전에 본문을 텍스트로 바꾼다. JSON 이면 파싱하고, 아니면 원문을 둔다.
export async function restoreBlobErrorBody(error: unknown): Promise<void> {
  if (!isAxiosError(error) || error.response === undefined) {
    return;
  }

  const data: unknown = error.response.data;
  if (!(data instanceof Blob)) {
    return;
  }

  try {
    const text = await data.text();
    error.response.data = parseJsonOrText(text);
  } catch {
    // 본문을 읽지 못하면 Blob 을 제거한다. 그래서 추출 로직이 Blob 을 다시 받지 않는다.
    error.response.data = null;
  }
}

function parseJsonOrText(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// Retry-After 는 초 단위 정수만 해석한다(백엔드 계약). HTTP-date 형식은 null 로 둔다.
function extractRetryAfterSeconds(headers: unknown): number | null {
  const raw: unknown = AxiosHeaders.from(headers as Parameters<typeof AxiosHeaders.from>[0]).get(
    "retry-after",
  );
  if (typeof raw !== "string" && typeof raw !== "number") {
    return null;
  }

  const seconds = Number(raw);
  if (!Number.isInteger(seconds) || seconds < 0) {
    return null;
  }

  return seconds;
}

function resolveHttpErrorKind(status: number | null, code: string | null): HttpErrorKind {
  if (code === "ECONNABORTED" || code === "ETIMEDOUT") {
    return "timeout";
  }

  if (status === null) {
    return "network";
  }

  if (status === 400 || status === 422) {
    return "badRequest";
  }

  if (status === 401) {
    return "unauthorized";
  }

  if (status === 403) {
    return "forbidden";
  }

  if (status === 404) {
    return "notFound";
  }

  if (status === 409) {
    return "conflict";
  }

  if (status >= 500) {
    return "server";
  }

  return "unknown";
}

function resolveHttpErrorMessage(
  kind: HttpErrorKind,
  status: number | null,
  serverMessage: string | null,
): string {
  if (serverMessage !== null && status !== null && status < 500) {
    return serverMessage;
  }

  switch (kind) {
    case "badRequest":
      return "요청 값이 올바르지 않습니다.";
    case "unauthorized":
      return "인증이 필요합니다.";
    case "forbidden":
      return "접근 권한이 없습니다.";
    case "notFound":
      return "요청한 리소스를 찾을 수 없습니다.";
    case "conflict":
      return "요청이 현재 서버 상태와 충돌합니다.";
    case "server":
      return "서버에서 요청을 처리하지 못했습니다.";
    case "network":
      return "서버에 연결하지 못했습니다.";
    case "timeout":
      return "요청 시간이 초과되었습니다.";
    case "unknown":
      return "요청을 완료하지 못했습니다.";
  }
}

function extractServerMessage(value: unknown): string | null {
  if (typeof value === "string" && value.trim() !== "") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(extractServerMessage).find((message) => message !== null) ?? null;
  }

  if (!isRecord(value)) {
    return null;
  }

  return (
    extractServerMessage(value.message) ??
    extractServerMessage(value.msg) ??
    extractServerMessage(value.error_description) ??
    extractServerMessage(value.error) ??
    extractServerMessage(value.detail)
  );
}

function extractServerCode(value: unknown): string | null {
  if (!isRecord(value) || typeof value.code !== "string") {
    return null;
  }

  const code = value.code.trim();
  return code !== "" ? code : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
